import axios from 'axios';

// Create axios instance with base configuration
const apiClient = axios.create({
  baseURL: 'https://a3d1kmonz2.execute-api.us-east-1.amazonaws.com/dev' || 'https://a3d1kmonz2.execute-api.us-east-1.amazonaws.com/dev',
});

// List of public endpoints that don't require authentication
const PUBLIC_ENDPOINTS = [
  '/outfits',
  '/search',
  '/trending',
  '/public',
  '/users/me/outfits', // Allow anonymous browsing of user outfits
  '/users/', // Allow anonymous access to user profiles (for outfit cards)
];

// List of endpoints that require authentication
const PROTECTED_ENDPOINTS = [
  '/like',
  '/unlike',
  '/follow',
  '/unfollow',
  '/upload',
  '/auth/',
  '/users/me/liked',
  '/users/me/following',
  '/users/me/followers',
  '/social/',
  '/rate',
  '/notifications'
];

// Check if URL is a public endpoint (allows anonymous access)
const isPublicEndpoint = (url: string): boolean => {
  // First check if it's explicitly protected
  if (PROTECTED_ENDPOINTS.some(endpoint => url.includes(endpoint))) {
    return false;
  }
  
  // Then check if it's explicitly public
  return PUBLIC_ENDPOINTS.some(endpoint => url.includes(endpoint));
};

// Add request interceptor to automatically include auth token for protected endpoints
apiClient.interceptors.request.use(
  async (config) => {
    try {
      const url = config.url || '';
      
      // CRITICAL: Never add Authorization header to S3 presigned URLs
      // Presigned URLs already contain credentials in the query string
      // Adding an Authorization header will break the signature
      if (url.includes('s3.amazonaws.com') || url.includes('s3.us-east-1.amazonaws.com')) {
        console.log('📦 S3 presigned URL detected, skipping auth header:', url.substring(0, 100) + '...');
        return config;
      }
      
      // Always try to add auth headers if available, but don't fail for public endpoints
      const enhancedAuthService = (await import('../services/enhanced-auth-service')).default;
      const isAuthenticated = await enhancedAuthService.isAuthenticated();
      
      if (isAuthenticated) {
        const token = await enhancedAuthService.getAccessToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
          console.log('🔐 Added auth header for:', url);
        }
      } else if (!isPublicEndpoint(url)) {
        // For protected endpoints without auth, let the request proceed
        // The backend will return 401 and the component can handle it
        console.log('⚠️ No auth available for protected endpoint:', url);
      } else {
        console.log('📖 Public endpoint, no auth needed:', url);
      }
    } catch (error) {
      console.warn('Failed to get auth token for:', config.url, error);
      // Continue without token - let the backend handle authentication
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor to handle authentication errors gracefully
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const url = error.config?.url || '';
    
    if (error.response?.status === 401) {
      if (isPublicEndpoint(url)) {
        console.log('📖 401 on public endpoint (should not happen):', url);
      } else {
        console.log('🔒 401 on protected endpoint (expected without auth):', url);
      }
      // Always let the component handle 401 errors appropriately
    }
    
    return Promise.reject(error);
  }
);

export default apiClient;